import { FC } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Transaction, useGetConsensusTransactionsTxHash } from '../../../oasis-nexus/api'
import { StyledDescriptionList } from '../../components/StyledDescriptionList'
import { PageLayout } from '../../components/PageLayout'
import { SubPageCard } from '../../components/SubPageCard'
import { useScreenSize } from '../../hooks/useScreensize'
import { AppErrors } from '../../../types/errors'
import { TextSkeleton } from '../../components/Skeleton'
import { useRequiredScopeParam } from '../../hooks/useScopeParam'
import { TransactionLink } from 'app/components/Transactions/TransactionLink'
import { CopyToClipboard } from 'app/components/CopyToClipboard'
import { StatusIcon } from 'app/components/StatusIcon'
import { BlockLink } from 'app/components/Blocks/BlockLink'
import { ConsensusTransactionMethod } from 'app/components/ConsensusTransactionMethod'
import { useFormattedTimestampStringWithDistance } from 'app/hooks/useFormattedTimestamp'
import { RoundedBalance } from 'app/components/RoundedBalance'
import { ConsensusAccountLink } from 'app/components/Account/ConsensusAccountLink'
import { CurrentFiatValue } from '../../components/CurrentFiatValue'
import { ConsensusTransactionEvents } from '../../components/Transactions/ConsensusTransactionEvents'
import { AllTokenPrices, useAllTokenPrices } from 'coin-gecko/api'
import { getFiatCurrencyForScope } from '../../../config'
import { useWantedTransaction } from '../../hooks/useWantedTransaction'
import { MultipleTransactionsWarning } from '../../components/Transactions/MultipleTransactionsWarning'
import { DashboardLink } from '../ParatimeDashboardPage/DashboardLink'
import { ConsensusEventTypeFilter } from '../../components/ConsensusEvents/ConsensusEventTypeFilter'
import { CardDivider } from '../../components/Divider'
import { useConsensusEventTypeParam } from '../../hooks/useCommonParams'
import { AdvancedField } from '../../components/AdvancedField/AdvancedField'
import { ToggleAdvancedFields } from '../../components/AdvancedField/ToggleAdvancedFields'

export const ConsensusTransactionDetailPage: FC = () => {
  const { isMobile } = useScreenSize()
  const { t } = useTranslation()
  const scope = useRequiredScopeParam()
  const hash = useParams().hash!
  const { isLoading, data } = useGetConsensusTransactionsTxHash(scope.network, hash)
  const tokenPrices = useAllTokenPrices(getFiatCurrencyForScope(scope))
  const { wantedTransaction: transaction, warningMultipleTransactionsSameHash } = useWantedTransaction(
    data?.data,
  )
  const { eventType, setEventType } = useConsensusEventTypeParam()
  if (!transaction && !isLoading) {
    throw AppErrors.NotFoundTxHash
  }

  return (
    <PageLayout>
      <MultipleTransactionsWarning enable={warningMultipleTransactionsSameHash} />
      <SubPageCard featured title={t('transaction.header')}>
        <ConsensusTransactionDetailView
          detailsPage
          isLoading={isLoading}
          tokenPrices={tokenPrices}
          transaction={transaction}
        />
      </SubPageCard>
      {transaction && (
        <AdvancedField>
          <SubPageCard
            title={t('common.events')}
            action={
              !isMobile && (
                <ConsensusEventTypeFilter
                  layer={transaction.layer}
                  value={eventType}
                  setValue={setEventType}
                />
              )
            }
          >
            {isMobile && (
              <>
                <ConsensusEventTypeFilter
                  layer={transaction.layer}
                  value={eventType}
                  setValue={setEventType}
                />
                <CardDivider />
              </>
            )}
            <ConsensusTransactionEvents transaction={transaction} eventType={eventType} />
          </SubPageCard>
        </AdvancedField>
      )}
    </PageLayout>
  )
}

export type TransactionDetailConsensusBlock = Transaction & {
  markAsNew?: boolean
}

export const ConsensusTransactionDetailView: FC<{
  isLoading?: boolean
  transaction: TransactionDetailConsensusBlock | undefined
  detailsPage?: boolean
  tokenPrices?: AllTokenPrices
  showLayer?: boolean
}> = ({ detailsPage, isLoading, showLayer, transaction, tokenPrices }) => {
  const { t } = useTranslation()
  const formattedTimestamp = useFormattedTimestampStringWithDistance(transaction?.timestamp)

  if (isLoading) return <TextSkeleton numberOfRows={detailsPage ? 13 : 7} />
  if (!transaction) return <></>

  const tokenPriceInfo = tokenPrices?.[transaction.ticker]

  return (
    <>
      <StyledDescriptionList standalone={!detailsPage} highlight={transaction.markAsNew}>
        {showLayer && (
          <>
            <dt>{t('common.paratime')}</dt>
            <dd>
              <DashboardLink scope={transaction} />
            </dd>
          </>
        )}
        <dt>{t('common.hash')}</dt>
        <dd>
          <TransactionLink scope={transaction} hash={transaction.hash} />
          <CopyToClipboard value={transaction.hash} />
        </dd>
        <dt>{t('common.status')}</dt>
        <dd style={{ flexWrap: 'wrap', gap: '10px' }}>
          <StatusIcon success={transaction.success} error={transaction.error} withText={true} />
        </dd>
        <dt>{t('common.type')}</dt>
        <dd className="p-0!">
          <ConsensusTransactionMethod method={transaction.method} />
        </dd>
        <dt>{t('common.timestamp')}</dt>
        <dd>{formattedTimestamp}</dd>
        <dt>{t('common.height')}</dt>
        <dd>
          <BlockLink scope={transaction} height={transaction.block} />
          <CopyToClipboard value={transaction.block.toString()} />
        </dd>
        <dt>{t('common.from')}</dt>
        <dd>
          <div className="inline-flex items-center">
            <ConsensusAccountLink
              network={transaction.network}
              address={transaction.sender}
              alwaysTrim={false}
            />
            <CopyToClipboard value={transaction.sender} />
          </div>
        </dd>
        {transaction.to && (
          <>
            <dt>{t('common.to')}</dt>
            <dd>
              <div className="inline-flex items-center">
                <ConsensusAccountLink
                  network={transaction.network}
                  address={transaction.to}
                  alwaysTrim={false}
                />
                <CopyToClipboard value={transaction.to} />
              </div>
            </dd>
          </>
        )}
        {transaction.amount && (
          <>
            <dt>{t('common.amount')}</dt>
            <dd>
              <RoundedBalance value={transaction.amount} ticker={transaction.ticker} />
            </dd>
          </>
        )}
        {detailsPage && (
          <>
            {transaction.amount &&
              !!tokenPriceInfo &&
              !tokenPriceInfo.isLoading &&
              (tokenPriceInfo.hasFailed ||
                (!tokenPriceInfo.isFree && tokenPriceInfo.price !== undefined)) && (
                <>
                  <dt>{t('currentFiatValue.title')}</dt>
                  <dd>
                    <CurrentFiatValue amount={transaction.amount} {...tokenPriceInfo} />
                  </dd>
                </>
              )}
            {transaction.body?.shares && (
              <>
                <dt>{t('common.shares')}</dt>
                <dd>
                  <RoundedBalance compactLargeNumbers value={transaction.body?.shares} />
                </dd>
              </>
            )}
            <dt>{t('common.fee')}</dt>
            <dd>
              <RoundedBalance value={transaction.fee} ticker={transaction.ticker} />
            </dd>

            <AdvancedField>
              <dt>{t('common.gasUsed')}</dt>
              <dd>{transaction.gas_used ? transaction.gas_used.toLocaleString() : t('common.missing')}</dd>
            </AdvancedField>

            {transaction.gas_limit && (
              <AdvancedField>
                <dt>{t('common.gasLimit')}</dt>
                <dd>
                  {t('common.valuePair', {
                    value: transaction.gas_limit,
                  })}
                </dd>
              </AdvancedField>
            )}

            <AdvancedField>
              <dt>{t('common.nonce')}</dt>
              <dd>{transaction.nonce.toLocaleString()}</dd>
            </AdvancedField>
          </>
        )}
      </StyledDescriptionList>
      <ToggleAdvancedFields />
    </>
  )
}
